import { Link, useNavigate } from "react-router-dom";
import styles from "./signup.module.css";
import { useEffect, useRef, useState } from "react";
import Loader from "../../../admin/components/Loader";
import { ArrowLeft } from "react-feather";
import axios from "axios";
import { StorageSetItem, STORAGE_KEYS } from "../../../admin/components/StorageComp";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { Controller, useForm } from "react-hook-form";
import { usePlacesWidget } from "react-google-autocomplete";
import { Checkbox, FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { CountryList } from "../../../admin/components/Constants";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";

const DOMAIN_SUFFIX = "." + process.env.REACT_APP_BASE_DOMAIN;

const COMPANY_SUFFIX_WORDS = [
  "private", "ltd", "limited", "llc", "inc", "incorporated",
  "corp", "corporation", "co", "company", "pvt", "plc",
  "shipping", "logistics", "group", "holdings", "enterprises",
]

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(null);
  const [formUserData, setFormUserData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [domainStatus, setDomainStatus] = useState(null);
  const domainManuallyEdited = useRef(false);
  const domainCheckTimer = useRef(null);

  const { websiteData } = useWebsite();

  useSiteBranding("Sign Up", websiteData);

  const themePrimaryColor = websiteData && websiteData?.home_config && websiteData?.home_config?.primaryColor ? websiteData?.home_config?.primaryColor : "#ef233c";
  const themeSecondaryColor = websiteData && websiteData?.home_config && websiteData?.home_config?.secondaryColor ? websiteData?.home_config?.secondaryColor : "#0a2a52";

  const { register, handleSubmit, setValue, control, watch, formState: { errors } } = useForm({
    mode: "onChange",
  });

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("company_address", place.formatted_address);

      if (place.address_components) {
        let address = "";
        let city = "";
        let postcode = "";

        place.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes("street_number")) {
            address = component.long_name;
          }

          if (types.includes("route")) {
            if (address) {
              address += " " + component.long_name;
            } else {
              address = component.long_name;
            }
          }

          if (types.includes("postal_town")) {
            city = component.long_name;
          }

          if (types.includes("locality") && !city) {
            city = component.long_name;
          }

          if (types.includes("postal_code")) {
            postcode = component.long_name;
          }

        });

        setValue("address", address);
        setValue("city", city);
        setValue("zip", postcode);
      }

      console.log(place);
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: 'GB' },
    },
  });

  async function onSubmit(data) {
    data = { ...data, url_token: data?.domain, domain: data?.domain ? `${data.domain}${DOMAIN_SUFFIX}` : "", website_template_id: 1 };

    setLoading(true);
    setErrorMsg("");

    return axios.post(process.env.REACT_APP_API_URL + "v1/signup", data).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setFormData(response.data.data);
        setFormUserData(response.data.userData);
        setStep(2);
      }
      if (response.data.status === "ERROR") {
        setErrorMsg(response?.data?.message);
      }
    }).catch((error) => {
      if (error && error?.response && error?.response?.data && error?.response?.data?.message) {
        setErrorMsg(error?.response?.data?.message);
      }
      setLoading(false);
    });
  }

  const handlePlanSelect = () => {
    setLoading(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/subscribe-free-trial", { company_id: formData?.id, user_id: formUserData?.id }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        const resData = response.data.data;
        StorageSetItem(STORAGE_KEYS.AUTH, resData, 24 * 60 * 60 * 1000);

        setTimeout(() => {
          navigate("/dashboard");
        }, 100);
      }
    }).catch((error) => {
      setLoading(false);
    });
  }

  const handleCustomPlanSelect = () => {
    setLoading(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/subscribe-custom", { company_id: formData?.id, user_id: formUserData?.id }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        const resData = response.data.data;
        StorageSetItem(STORAGE_KEYS.AUTH, resData, 24 * 60 * 60 * 1000);

        setTimeout(() => {
          navigate("/dashboard");
        }, 100);
      }
    }).catch((error) => {
      setLoading(false);
    });
  }

  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          description: "Purchage Subscribe Plan",
          amount: {
            currency_code: "GBP",
            value: selectedPlan?.monthly_amount,
          },
        },
      ],
      application_context: {
        shipping_preference: "NO_SHIPPING",
      },
    }).then((orderID) => {
      return orderID;
    });
  };

  const onApprove = (data, actions) => {
    setLoading(true);
    return actions.order.capture().then(function (details) {
      let postData = { company_id: formData?.id, user_id: formUserData?.id };
      postData = { ...postData, subscription_id: selectedPlan?.id, total_booking: 0, payment_amount: details?.purchase_units[0]?.payments?.captures[0]?.amount?.value, transaction_id: details?.purchase_units[0]?.payments?.captures[0]?.id, payer_id: details?.payer?.payer_id, payer_name: details?.payer?.name?.given_name + ' ' + details?.payer?.name?.surname, payer_email: details?.payer?.email_address };

      axios.post(process.env.REACT_APP_API_URL + "v1/signup-subscription", postData).then((response) => {
        const resData = response.data.data;
        StorageSetItem(STORAGE_KEYS.AUTH, resData, 24 * 60 * 60 * 1000);

        setTimeout(() => {
          navigate("/dashboard");
        }, 100);
      }).catch((error) => {
        setLoading(false);
      });
    });
  }

  const slugify = (value) => {
    if (!value) return "";

    const words = value.trim().split(/\s+/).map((w) => w.toLowerCase().replace(/[^a-z0-9]/g, "")).filter(Boolean);

    // Drop trailing/noise words like "shipping", "private", "ltd" etc.
    const meaningfulWords = words.filter((w) => !COMPANY_SUFFIX_WORDS.includes(w));

    const base = meaningfulWords.length > 0 ? meaningfulWords : words;

    // Keep it short — just the first meaningful word
    return base[0] || "";
  }

  const companyNameValue = watch("company_name");

  useEffect(() => {
    if (domainManuallyEdited.current) return;
    const slug = slugify(companyNameValue || "");
    setValue("domain", slug, { shouldValidate: true });
    checkDomainAvailability(slug);
  }, [companyNameValue]);

  const checkDomainAvailability = (value) => {
    if (domainCheckTimer.current) clearTimeout(domainCheckTimer.current);

    if (!value || value.length < 3) {
      setDomainStatus(null);
      return;
    }

    setDomainStatus("checking");

    domainCheckTimer.current = setTimeout(() => {
      axios.post(process.env.REACT_APP_API_URL + "v1/check-domain", {
        domain: `${value}${DOMAIN_SUFFIX}`,
      }).then((response) => {
        if (response.data.status === "SUCCESS") {
          setDomainStatus(response.data.data?.is_available ? "available" : "taken");
        } else {
          setDomainStatus("error");
        }
      }).catch(() => {
        setDomainStatus("error");
      });
    }, 500);
  }

  const handleDomainChange = (e, onChange) => {
    domainManuallyEdited.current = true;
    let val = e.target.value.toLowerCase();
    val = val.replace(/[^a-z0-9-]/g, "");
    val = val.replace(/^-+/, "");
    onChange(val);
    checkDomainAvailability(val);
  }

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <div className={styles.MainDiv} style={{ "--primary-color": themePrimaryColor, "--secondary-color": themeSecondaryColor }}>
      <div className={styles.LoginFormSec}>
        <div className={styles.LohinForm}>
          <Link to={"/"} className={styles.BackHomeBU}>
            <ArrowLeft size={18} />
            Back to home
          </Link>

          <h2 className={styles.Heading}>Sign Up</h2>
          {step === 1 && <p className={styles.LoginformSmTitle}>Create an account to get started</p>}
          {step === 2 && <p className={styles.LoginformSmTitle}>Choose your subscription plan</p>}
          {step === 3 && <p className={styles.LoginformSmTitle}>Pay your amount</p>}

          {step === 1 && <form onSubmit={handleSubmit(onSubmit)}>
            <input type="hidden" {...register("address")} />
            <input type="hidden" {...register("city")} />
            <input type="hidden" {...register("zip")} />

            {errorMsg && (<p className={styles.ErrorM2}>{errorMsg}</p>)}

            <div className={styles.FormRow}>
              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("company_name", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Company Name*"
                    variant="outlined"
                  />)}
                />
                {errors.company_name && (<span className={styles.ErrorM}>{errors?.company_name?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("domain", {
                    required: "This field is Required",
                    minLength: { value: 3, message: "Must be at least 3 characters" },
                    pattern: {
                      value: /^[a-z0-9]+(-[a-z0-9]+)*$/,
                      message: "Lowercase letters, numbers and hyphens only",
                    },
                    validate: async (value) => {
                      if (!value) return true; // required rule already covers empty
                      try {
                        const response = await axios.post(process.env.REACT_APP_API_URL + "v1/check-domain", {
                          domain: `${value}${DOMAIN_SUFFIX}`,
                        });
                        if (response.data.status === "SUCCESS" && response.data.data?.is_available) {
                          return true;
                        }
                        return "This domain is already taken";
                      } catch (error) {
                        return "Could not verify domain availability, please try again";
                      }
                    },
                  })}
                  render={({ field }) => (<TextField
                    {...field}
                    onChange={(e) => handleDomainChange(e, field.onChange)}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Domain*"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (<InputAdornment position="end" className={styles.DomainSuffix}>
                        {"." + process.env.REACT_APP_BASE_DOMAIN}
                      </InputAdornment>),
                    }}
                  />)}
                />
                {errors.domain ? (<span className={styles.ErrorM}>{errors?.domain?.message}</span>) : domainStatus === "checking"
                  ? (<span className={styles.FieldHint}>Checking availability…</span>) : domainStatus === "available" ? (<span className={styles.FieldSuccess}>This domain is available</span>) : domainStatus === "taken" ? (<span className={styles.ErrorM}>This domain is already taken</span>) : (<span className={styles.FieldHint}>Your booking portal will be available at this address</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("company_address", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Company Address*"
                    variant="outlined"
                    placeholder=""
                    inputRef={ref}
                  />)}
                />
                {errors.company_address && (<span className={styles.ErrorM}>{errors?.company_address?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("name", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Owner Name*"
                    variant="outlined"
                  />)}
                />
                {errors.name && (<span className={styles.ErrorM}>{errors?.name?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("email", {
                    required: "This field is Required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Email*"
                    variant="outlined"
                  />)}
                />
                {errors.email && (<span className={styles.ErrorM}>{errors.email.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("phone", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Phone*"
                    variant="outlined"
                  />)}
                />
                {errors.phone && (<span className={styles.ErrorM}>{errors.phone.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  {...register("password", { required: "This field is Required" })}
                  control={control}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Password*"
                    variant="outlined"
                    type="password"
                  />)}
                />
                {errors.password && (<span className={styles.ErrorM}>{errors.password.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  {...register("conf_password", {
                    required: "This field is Required",
                    validate: (val) => {
                      if (watch("password") !== val) {
                        return "Your passwords do no match";
                      }
                    },
                  })}
                  control={control}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Confirm Password*"
                    variant="outlined"
                    type="password"
                  />)}
                />
                {errors.conf_password && (<span className={styles.ErrorM}>{errors?.conf_password?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <FormControl fullWidth>
                  <InputLabel id="pickup-country-label">Pickup Country*</InputLabel>
                  <Controller
                    control={control}
                    {...register("pickup_country", { required: "This field is required" })}
                    render={({ field }) => (<Select
                      labelId="pickup-country-label"
                      label="Pickup Country*"
                      classes={{ root: styles.selectRoot }}
                      {...field}
                    >
                      <MenuItem value={"GB"}>United Kingdom</MenuItem>
                    </Select>)}
                  />
                </FormControl>
                {errors.pickup_country && (<span className={styles.ErrorM}>{errors?.pickup_country?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <FormControl fullWidth>
                  <InputLabel id="delivery-country-label">Delivery Country*</InputLabel>
                  <Controller
                    control={control}
                    {...register("delivery_country", { required: "This field is required" })}
                    render={({ field }) => (<Select
                      labelId="delivery-country-label"
                      label="Delivery Country*"
                      classes={{ root: styles.selectRoot }}
                      {...field}
                    >
                      {CountryList?.map((i) => {
                        return (<MenuItem key={i?.value} value={i?.value}>{i?.label}</MenuItem>);
                      })}
                    </Select>)}
                  />
                </FormControl>
                {errors.delivery_country && (<span className={styles.ErrorM}>{errors?.delivery_country?.message}</span>)}
              </div>

              <div className={styles.FormGroupFull}>
                <FormControl fullWidth className={styles.TermsRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register('terms', { required: 'You must accept the terms and conditions' })}
                        error={!!errors.terms}
                      />
                    }
                    label={<>
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer" className={styles.ForhotPassText}>
                        Terms and Conditions
                      </Link>
                    </>
                    }
                  />
                </FormControl>
                {errors.terms && (<span className={styles.ErrorM}>{errors?.terms?.message}</span>)}
              </div>
            </div>

            <button type="submit" className={styles.LoginBU}>
              <span>Register</span>
            </button>

            <p className={styles.LoginFormSText}>
              Already have an account? <Link to={"/sign-in"} className={styles.ForhotPassText}>Login</Link>
            </p>
          </form>}

          {step === 2 && <div className={styles.PlanWrapper}>

            <div className={styles.PlanGrid}>

              {/* FREE TRIAL PLAN */}
              <div className={`${styles.PlanCard} ${styles.FreeTrialCard}`}>
                <div className={styles.TrialBadge}>FREE</div>
                <h3>7 Day Free Trial</h3>
                <h1>£0</h1>

                <ul>
                  <li>✓ Bookings</li>
                  <li>✓ Customers</li>
                  <li>✓ Items</li>
                  <li>✓ Customer Portal</li>
                </ul>

                <button type="button" className={styles.FreeTrialBtn} onClick={() => handlePlanSelect()}>Start Free Trial</button>
              </div>

              {/* STANDARD */}
              <div className={styles.PlanCard}>
                <h3>Standard</h3>
                <h1>£350 <span>/Month</span></h1>

                <ul>
                  <li>✓ Bookings</li>
                  <li>✓ Customers</li>
                  <li>✓ Items</li>
                  <li>✓ Customer Portal</li>
                </ul>

                <button type="button" className={styles.SelectPlanBtn} onClick={() => { setSelectedPlan({ id: 2, name: 'Standard', monthly_amount: 350 }); setStep(3); }}>Subscribe</button>
              </div>
            </div>

            <div className={styles.PlanGrid}>

              {/* PREMIUM */}
              <div className={`${styles.PlanCard} ${styles.Recommended}`}>
                <div className={styles.Badge}>Most Popular</div>
                <h3>Premium</h3>
                <h1>£513 <span>/Month</span></h1>

                <ul>
                  <li>✓ Everything in Standard</li>
                  <li>✓ Driver Management</li>
                  <li>✓ Shipping & Delivery</li>
                  <li>✓ Apps Included</li>
                </ul>

                <button type="button" className={styles.SelectPlanBtn} onClick={() => { setSelectedPlan({ id: 3, name: 'Premium', monthly_amount: 513 }); setStep(3); }}>Subscribe</button>
              </div>

              {/* ENTERPRISE */}
              <div className={styles.PlanCard}>
                <h3>Full</h3>
                <h1>£2518 <span>/Month</span></h1>

                <ul>
                  <li>✓ Everything in Premium</li>
                  <li>✓ Referral System</li>
                  <li>✓ Membership Benefits</li>
                  <li>✓ 24/7 Support</li>
                </ul>

                <button type="button" className={styles.SelectPlanBtn} onClick={() => { setSelectedPlan({ id: 4, name: 'Full', monthly_amount: 2518 }); setStep(3); }}>Subscribe</button>
              </div>

            </div>
            <div className={styles.PlanGrid} style={{ justifyContent: 'center' }}>

              {/* CUSTOM PLAN */}
              <div className={`${styles.PlanCard} ${styles.CustomCard}`}>
                <div className={styles.EnterpriseBadge}>Custom</div>

                <h3>Custom Plan</h3>
                <h1>Custom Pricing</h1>

                <p className={styles.CustomText}>
                  Tailored logistics solution for large scale operations
                </p>

                <button
                  type="button"
                  className={styles.ContactBtn}
                  onClick={() => handleCustomPlanSelect()}
                >
                  Contact Sales
                </button>
              </div>

            </div>
          </div>}

          {step === 3 && <div className={styles.SubscribePaySec}>
            <p className={styles.PlanPayTitle}>Plan: {selectedPlan?.name}</p>
            <p className={styles.PlanPayAmount}>Payable Amount: &pound;{selectedPlan?.monthly_amount}</p>

            <PayPalButtons style={{ layout: "vertical" }} createOrder={createOrder} onApprove={onApprove} />
          </div>}

        </div>
      </div>

      <div className={styles.LoginBGSec}>
        <img src="/images/login-side-banner.png" alt="Logistics" />
        <div className={styles.Overlay}></div>

        <div className={styles.PlanTeaser}>
          <h3>Plans that scale with your fleet</h3>
          <p>Start free, upgrade any time. No setup fees, no long-term contracts.</p>
        </div>

        <div className={styles.TeaserPlanWrap}>
          <div className={styles.TeaserPlanItem}>
            <div>
              <p className={styles.TeaserPlanName}>Free Trial</p>
              <p className={styles.TeaserPlanDesc}>7 days, full access</p>
            </div>
            <div className={styles.TeaserPlanPrice}>£0</div>
          </div>

          <div className={`${styles.TeaserPlanItem} ${styles.TeaserFeatured}`}>
            <div>
              <p className={styles.TeaserPlanName}>Premium</p>
              <p className={styles.TeaserPlanDesc}>Most popular choice</p>
            </div>
            <div className={styles.TeaserPlanPrice}>£513<span>/mo</span></div>
          </div>

          <div className={styles.TeaserPlanItem}>
            <div>
              <p className={styles.TeaserPlanName}>Full</p>
              <p className={styles.TeaserPlanDesc}>Enterprise grade</p>
            </div>
            <div className={styles.TeaserPlanPrice}>£2,518<span>/mo</span></div>
          </div>
        </div>
      </div>
    </div>
  </>);
}

export default Signup;